import type { Email as BaseEmail } from '../systems/EventBus';
import type { ReplyChallenge } from './reply-templates';

export type EmailCategory =
  | 'hr'
  | 'cfo'
  | 'customer-reply'
  | 'dispute-followup'
  | 'internal'
  | 'spam'
  | 'voicemail';

// Narrows BaseEmail.category from string to EmailCategory. Assignable back
// to BaseEmail (narrow → broad), so bus.emit('email:received', ...) stays
// type-safe. BaseEmail itself lives in EventBus so the systems layer has
// no dependency on content.
export interface Email extends Omit<BaseEmail, 'category'> {
  category: EmailCategory;
}

export interface EmailTemplate {
  subject: string;
  from: string;
  body: string;
  category: EmailCategory;
  replyChallenge?: ReplyChallenge;
}

export const PROCEDURAL_EMAIL_POOL: EmailTemplate[] = [
  {
    subject: 'Re: Invoice 1045 — payment question',
    from: 'alicia@brightventures.com',
    body: 'Hi — quick question on invoice 1045. Is the due date Net 30 from invoice date or receipt date? Also, what\'s our total open with you?',
    category: 'customer-reply',
    replyChallenge: {
      lookupHint: 'Check Bright Ventures\' open invoices in the AR aging spreadsheet.',
      options: [
        {
          id: 'bright-correct',
          body: 'Net 30 from invoice date. You\'re showing $12.1K open across four invoices, oldest INV-6001 at 97 days — please prioritize that one.',
          correct: true,
        },
        {
          id: 'bright-zero',
          body: 'Net 30 from receipt date. You\'re current with us — nothing outstanding right now.',
          correct: false,
        },
        {
          id: 'bright-low',
          body: 'Net 30 from invoice date. We show one open invoice at $1,200 — that should be it.',
          correct: false,
        },
      ],
    },
  },
  {
    subject: 'Payment sent — wire #88401',
    from: 'ap@peakindustries.com',
    body: 'Wire #88401 sent today for $18,000. What invoices does that cover?',
    category: 'customer-reply',
    replyChallenge: {
      lookupHint: 'Look up Peak Industries\' open invoices in the AR aging spreadsheet to figure out what $18K covers.',
      options: [
        {
          id: 'peak-wire-correct',
          body: 'Confirmed receipt. $18K applies cleanly to your three oldest: INV-2003 ($5.2K) + INV-2011 ($7.5K) + INV-2027 ($4.1K) + $1.2K partial on INV-2034.',
          correct: true,
        },
        {
          id: 'peak-wire-single',
          body: 'Got it — applying the full $18K to INV-2003 (oldest at 73 days).',
          correct: false,
        },
        {
          id: 'peak-wire-overpay',
          body: 'Confirmed. $18K covers your last two invoices in full and credits the rest to your account.',
          correct: false,
        },
      ],
    },
  },
  {
    subject: 'Dispute update — PO 4412',
    from: 'ops@coresystems.com',
    body: 'Still researching on our side. Will circle back this week. Remind me what amount we\'re holding?',
    category: 'dispute-followup',
    replyChallenge: {
      lookupHint: 'Find Core Systems\' disputed invoice in the AR aging spreadsheet (status column).',
      options: [
        {
          id: 'core-correct',
          body: 'INV-5003 — $4,400 disputed at 89 days. Holding payment until your team resolves PO 4412.',
          correct: true,
        },
        {
          id: 'core-wrong-amt',
          body: 'INV-5003 — $6,100 disputed at 29 days. Standing by for your update.',
          correct: false,
        },
        {
          id: 'core-clean',
          body: 'No active disputes on file with Core Systems — looks like PO 4412 was closed last week.',
          correct: false,
        },
      ],
    },
  },
  {
    subject: 'quick q on the Acme account',
    from: 'priya.c@cascadefoods.com',
    body: 'where are we on Acme 90+? flagged it again this morning.',
    category: 'cfo',
  },
  {
    subject: "Marcus here — can you apply the Peak wire?",
    from: 'marcus.b@cascadefoods.com',
    body: 'hey — Peak wire came in, mind applying it when you get a sec?',
    category: 'internal',
  },
  {
    subject: "You've Won a Caribbean Cruise!!!",
    from: 'no-reply@rewardszone.example',
    body: 'Click here to claim your vacation prize!',
    category: 'spam',
  },
  {
    subject: 'Re: past due notice',
    from: 'accounts@deltaco.com',
    body: 'we mailed a check last Friday. should be there early next week. what was the total?',
    category: 'customer-reply',
    replyChallenge: {
      lookupHint: 'Look up Delta Retail\'s open balance in the AR aging spreadsheet.',
      options: [
        {
          id: 'delta-correct',
          body: 'You\'re showing $24.1K open across five invoices, oldest INV-3002 at $800 / 118 days. Apply oldest-first when the check clears.',
          correct: true,
        },
        {
          id: 'delta-wrong',
          body: 'You\'re at $4,200 on a single invoice. Will apply when the check clears.',
          correct: false,
        },
        {
          id: 'delta-current',
          body: 'You\'re current — the check should clear into prepaid balance.',
          correct: false,
        },
      ],
    },
  },
  {
    subject: 'AR report distribution list',
    from: 'hr@cascadefoods.com',
    body: 'Please confirm your distribution list for the month-end AR snapshot.',
    category: 'internal',
  },
  {
    subject: 'Invoice 4007 — short pay?',
    from: 'ap@techstart.com',
    body: 'We reviewed invoice 4007 and the PO shows $4,200 not $5,000. Paying $4,200. Confirm?',
    category: 'customer-reply',
    replyChallenge: {
      lookupHint: 'Look up INV-4007 in the AR aging spreadsheet — what\'s the actual amount on file?',
      options: [
        {
          id: 'inv4007-correct',
          body: 'INV-4007 is on file at $5,000 — the $800 difference flags as a short-pay. Logging the dispute and routing to the PO team.',
          correct: true,
        },
        {
          id: 'inv4007-agree',
          body: 'Confirmed — our system also shows $4,200. Apologies for the confusion. Apply $4,200 in full settlement.',
          correct: false,
        },
        {
          id: 'inv4007-zero',
          body: 'INV-4007 is not in our system. Can you resend the original invoice number?',
          correct: false,
        },
      ],
    },
  },
  {
    subject: 'Lunch coverage this week',
    from: 'marcus.b@cascadefoods.com',
    body: 'can you cover the 12-1 phones on Wednesday? I have a dentist thing.',
    category: 'internal',
  },
];

export const OPENING_INBOX: Email[] = [
  {
    id: 'op-hr-1',
    subject: 'Welcome to your first month-end close!',
    from: 'hr@cascadefoods.com',
    body: "Today's your first close day. Jamie will be in Slack to help. Take a breath.",
    receivedAt: '8:42 AM',
    read: false,
    category: 'hr',
  },
  {
    id: 'op-cfo-1',
    subject: 'Acme 90+ — any progress?',
    from: 'priya.c@cascadefoods.com',
    body: 'What are we doing about Acme? Need a plan by EOD.',
    receivedAt: '7:15 PM (yesterday)',
    read: false,
    category: 'cfo',
    replyChallenge: {
      lookupHint: 'Open the AR aging spreadsheet → check Acme\'s 90+ bucket before answering.',
      options: [
        {
          id: 'acme-correct',
          body: 'Acme is at $42K in 90+ across four invoices. Pushing collections referral on the oldest, expecting a check on INV-1031.',
          correct: true,
        },
        {
          id: 'acme-low',
          body: 'Acme is at $28K in 90+ on two invoices. Past-due notice going out today.',
          correct: false,
        },
        {
          id: 'acme-current',
          body: 'Acme is current — last check cleared Friday. Nothing in 90+.',
          correct: false,
        },
      ],
    },
  },
  {
    id: 'op-cfo-2',
    subject: 'Bank statements reconciled?',
    from: 'priya.c@cascadefoods.com',
    body: 'Did Thursday close out cleanly?',
    receivedAt: '8:01 PM (yesterday)',
    read: false,
    category: 'cfo',
  },
  {
    id: 'op-cfo-3',
    subject: 'DSO trending — talk tomorrow',
    from: 'priya.c@cascadefoods.com',
    body: 'DSO crept up again. Pull numbers for our 9:30.',
    receivedAt: '8:15 PM (yesterday)',
    read: false,
    category: 'cfo',
  },
  {
    id: 'op-cfo-4',
    subject: 'Peak Industries status',
    from: 'priya.c@cascadefoods.com',
    body: 'wire come in yet? what\'s outstanding?',
    receivedAt: '8:34 PM (yesterday)',
    read: false,
    category: 'cfo',
    replyChallenge: {
      lookupHint: 'Check Peak Industries in the AR aging spreadsheet — what\'s their open balance?',
      options: [
        {
          id: 'peak-correct',
          body: 'Wire hasn\'t landed yet. Peak\'s sitting at $35.7K open across six invoices, oldest 73 days. Will apply when it arrives.',
          correct: true,
        },
        {
          id: 'peak-applied',
          body: 'Wire arrived this morning, $18K applied to oldest two invoices. Account is current now.',
          correct: false,
        },
        {
          id: 'peak-current',
          body: 'Peak is current — they paid in full last week. Nothing outstanding.',
          correct: false,
        },
      ],
    },
  },
  {
    id: 'op-cfo-5',
    subject: 'Delta write-off',
    from: 'priya.c@cascadefoods.com',
    body: 'we talked about the Delta write-off — remind me which line?',
    receivedAt: '9:12 PM (yesterday)',
    read: false,
    category: 'cfo',
    replyChallenge: {
      lookupHint: 'Find Delta Retail\'s oldest line in the AR aging spreadsheet.',
      options: [
        {
          id: 'delta-correct',
          body: 'INV-3002 — $800 at 118 days. Small enough to write off cleanly. Lining up Marcus\'s sign-off today.',
          correct: true,
        },
        {
          id: 'delta-bigger',
          body: 'INV-3015 — $4,200 at 71 days. Pulling the dispute history before approving.',
          correct: false,
        },
        {
          id: 'delta-none',
          body: 'Delta\'s current — paid the last invoice Friday. No write-off needed.',
          correct: false,
        },
      ],
    },
  },
  {
    id: 'op-cfo-6',
    subject: 'Q1 close deadline',
    from: 'priya.c@cascadefoods.com',
    body: 'Friday. Hard.',
    receivedAt: '10:03 PM (yesterday)',
    read: false,
    category: 'cfo',
  },
  {
    id: 'op-rep-1',
    subject: 'Re: past due #1 notice',
    from: 'ar@atlastrading.com',
    body: 'payment processing on our end, thanks for the nudge. what was outstanding again?',
    receivedAt: '6:08 AM',
    read: false,
    category: 'customer-reply',
    replyChallenge: {
      lookupHint: 'Look up Atlas Trading\'s open balance in the AR aging spreadsheet.',
      options: [
        {
          id: 'atlas-correct',
          body: 'Confirmed — you\'re showing $28.5K outstanding across six invoices, oldest INV-7002 at 109 days. Will apply when the wire lands.',
          correct: true,
        },
        {
          id: 'atlas-low',
          body: 'You\'re at $12K on a single invoice from last month. Apply on receipt.',
          correct: false,
        },
        {
          id: 'atlas-zero',
          body: 'We show $0 outstanding — looks like you\'re already current. Maybe payment crossed in transit?',
          correct: false,
        },
      ],
    },
  },
  {
    id: 'op-rep-2',
    subject: 'Re: past due #1 notice',
    from: 'billing@northstar.com',
    body: "will pay today — what's the remit address again?",
    receivedAt: '6:44 AM',
    read: false,
    category: 'customer-reply',
  },
  {
    id: 'op-rep-3',
    subject: 'Re: final notice',
    from: 'cfo@summitpartners.com',
    body: 'can we hop on a call this week? cash is tight.',
    receivedAt: '7:12 AM',
    read: false,
    category: 'customer-reply',
  },
  {
    id: 'op-rep-4',
    subject: 'Re: collections referral notice',
    from: 'legal@westfieldco.com',
    body: 'please hold — we dispute the 03/22 invoice amount.',
    receivedAt: '7:50 AM',
    read: false,
    category: 'customer-reply',
  },
  {
    id: 'op-disp-1',
    subject: 'Dispute follow-up — PO 3308',
    from: 'ops@bayviewlogistics.com',
    body: 'any update on the $1,400 short-pay from last month?',
    receivedAt: '7:32 AM',
    read: false,
    category: 'dispute-followup',
  },
  {
    id: 'op-disp-2',
    subject: 'Dispute — duplicate charge',
    from: 'billing@greenfield.com',
    body: 'invoices 994 and 1017 look like the same shipment. please confirm.',
    receivedAt: '7:48 AM',
    read: false,
    category: 'dispute-followup',
  },
  {
    id: 'op-disp-3',
    subject: 'Re: credit memo request',
    from: 'ap@ridgeline.com',
    body: 'still waiting on the credit memo for the 03/14 short-ship.',
    receivedAt: '8:02 AM',
    read: false,
    category: 'dispute-followup',
  },
  {
    id: 'op-disp-4',
    subject: 'Dispute update — sales tax',
    from: 'tax@harborco.com',
    body: 'we exempt for MA, please issue a credit.',
    receivedAt: '8:15 AM',
    read: false,
    category: 'dispute-followup',
  },
  {
    id: 'op-disp-5',
    subject: 'Re: past due disputed amount',
    from: 'ap@lakeview.com',
    body: 'waiting on your PO team to circle back.',
    receivedAt: '8:27 AM',
    read: false,
    category: 'dispute-followup',
  },
  {
    id: 'op-spam-1',
    subject: 'Tax-optimize your AR pipeline with AI!',
    from: 'sales@verynew.example',
    body: '...',
    receivedAt: '5:12 AM',
    read: false,
    category: 'spam',
  },
  {
    id: 'op-spam-2',
    subject: '🔥 Limited time: collections course 50% off',
    from: 'edu@collectmaster.example',
    body: '...',
    receivedAt: '6:01 AM',
    read: false,
    category: 'spam',
  },
  {
    id: 'op-spam-3',
    subject: 'Your printer cartridge is low',
    from: 'no-reply@printerco.example',
    body: 'Reorder now.',
    receivedAt: '7:55 AM',
    read: false,
    category: 'spam',
  },
  {
    id: 'op-int-1',
    subject: 'Coffee run?',
    from: 'marcus.b@cascadefoods.com',
    body: 'heading down in 20 — pour over?',
    receivedAt: '8:38 AM',
    read: false,
    category: 'internal',
  },
  {
    id: 'op-int-2',
    subject: 'Remittance form — Peak',
    from: 'marcus.b@cascadefoods.com',
    body: "Peak's AP said they're wiring today. heads up.",
    receivedAt: '8:44 AM',
    read: false,
    category: 'internal',
  },
  {
    id: 'op-int-3',
    subject: 'Aging snapshot for Friday',
    from: 'controller@cascadefoods.com',
    body: 'send by EOD Thursday for the Friday close review.',
    receivedAt: '8:51 AM',
    read: false,
    category: 'internal',
  },
  {
    id: 'op-int-4',
    subject: 'Security training reminder',
    from: 'it@cascadefoods.com',
    body: 'Phishing module due by EOM.',
    receivedAt: '8:59 AM',
    read: false,
    category: 'internal',
  },
];
